/**
 * HubStructure — renders `DistributionStyle.HUB`.
 *
 * `Distribution.ts:82` declares that value with the note *"Renderer not yet
 * built; the value is authorable so the choice is recorded ahead of it."* This
 * is that renderer.
 *
 * ## What this component is, and is not
 *
 * It positions BOXES. It never looks inside one. A slot arrives with a role and
 * a footprint; this decides where that footprint lands and how it behaves when
 * the viewport or the occupancy changes. What renders inside is the widget
 * layer's business, passed in as `renderSlot`.
 *
 * That boundary is the whole point of the project: the structure is built
 * ONCE and every POV — publisher, brand, esports, collegiate, retail, a brand's
 * own campaign — is tokens plus slot config on top of it. If this file ever
 * needs to know which brand it is rendering, the layer model has been broken.
 *
 * ## DOM order is load-bearing, twice
 *
 * 1. Column widgets are emitted BEFORE the bands. A right float only has
 *    content to flow past it if that content comes after it.
 * 2. The column is emitted before the stage so the float has somewhere to go —
 *    and at ≤1180px `order` puts it back after the stage, because with floats
 *    off it would otherwise be the first thing on the page.
 */

/*
 * ── THE CLASS NAMES ARE THE ANCHOR'S, AND OURS ──
 *
 * Every element below carries the anchor's name first (`rail`, `content`,
 * `mid`, `wrap`) and our BEM name second. That is not indecision.
 *
 * `HubSpine.scss` is `publisher-drop-hub.html`'s stylesheet ported rule for
 * rule, and it styles the anchor's names. This file previously emitted only
 * `surface-hub__rail` etc., so the ported sheet matched nothing and the page
 * kept rendering as a generic stack of boxes that happened to measure right.
 * Emitting the anchor's names is what connects the two.
 *
 * The BEM names stay because they are what the structure's own tests and the
 * `data-occupancy` consumers select on, and because they say which box is
 * which without knowing the anchor. Renaming the anchor's classes to match
 * ours would mean editing 274 ported rules — every one of which carries a
 * comment recording a layout failure already paid for once.
 */
import React from "react";
import {SurfaceSlot, SlotFootprint, activeSlots} from "../slots";
import {OccupancyState, StageState, resolveOccupancy, occupancySignal} from "../occupancy";
/*
 * THE TOKEN CONTRACT, IMPORTED WHERE IT IS USED.
 *
 * `tokens.scss` defines every name the ported spine reads — `--band`, `--colh`,
 * `--p1`, `--tx`, `--o`, `--g`, `--r` — and it was imported by NOTHING. So
 * `height:var(--band)` resolved to an invalid value and fell back to `auto`,
 * the rail and the stage sized to their contents instead of to each other, and
 * every panel painted with no background. The structure looked like a stack of
 * grey boxes, which is exactly what an unstyled version of it is.
 */
import "../tokens.scss";
/*
 * `HubStructure.scss` is deliberately NOT imported.
 *
 * It was this structure's own stylesheet, written before the anchor was
 * ported. Both sheets style the same elements — every box carries the anchor's
 * class and ours — so loading both means two sets of rules competing at equal
 * specificity, decided by import order. Measured: the stage landed BELOW the
 * rail instead of beside it, because the old sheet's `.surface-hub__content`
 * overrode the float model the spine depends on.
 *
 * `HubSpine.scss` is the complete stylesheet of the anchor this must match, so
 * it is the one that governs. The old file stays on disk until the last thing
 * selecting its class names is gone.
 */
import "../spine/HubSpine.scss";

export interface HubStructureProps {
    slots: readonly SurfaceSlot[];
    stage?: StageState;
    /**
     * The surface's own bar, above everything. Rendered outside `.wrap` because
     * the anchor's `header.hd` is a sibling of it, not a child — it spans the
     * full width while `.wrap` carries the page's 26px gutters.
     */
    chrome?: React.ReactNode;
    /** Pinned below the page. The anchor's `nav.ab`. */
    actionBar?: React.ReactNode;
    /** The viewer's chosen column occupant. May be stale; the resolver handles it. */
    pinnedSlotId?: string | null;
    /**
     * Renders a slot's occupant. Structure never looks inside — this is the
     * seam the widget layer plugs into.
     */
    renderSlot: (slot: SurfaceSlot, occupancy: OccupancyState) => React.ReactNode;
    /** The occupant control, rendered UNDER the content it switches (§3). */
    renderColumnControl?: (occupancy: OccupancyState) => React.ReactNode;
}

const byFootprint = (slots: SurfaceSlot[], footprint: SlotFootprint) =>
    slots.filter(s => s.footprint === footprint);

export const HubStructure = ({
    slots,
    stage = StageState.IDLE,
    pinnedSlotId,
    chrome,
    actionBar,
    renderSlot,
    renderColumnControl,
}: HubStructureProps) => {
    /*
     * ── WHERE THE FIXED HEADER STARTS ──
     *
     * The surface's own header is fixed, which is right when the hub owns the
     * viewport — PRIVATE and CUSTOM_DOMAIN, where there is no platform chrome
     * at all. On the DIRECTORY channel the DevourPlay navbar is also pinned to
     * the top, so `top: 0` puts ours directly on top of theirs.
     *
     * The offset is MEASURED, never a constant. A hardcoded nav height is wrong
     * the first time that bar changes and wrong in every embed that does not
     * have one, and nothing would catch either. What we want is simply "where
     * does this surface begin", which is exactly what the surface's own offset
     * from the document top already is — 0 when standalone, the chrome's height
     * when wrapped.
     *
     * Re-measured on resize because the platform bar wraps at narrow widths.
     */
    const rootRef = React.useRef<HTMLDivElement>(null);
    const [chromeTop, setChromeTop] = React.useState(0);

    React.useLayoutEffect(() => {
        const measure = () => {
            const el = rootRef.current;
            if (!el) return;
            const top = el.getBoundingClientRect().top + window.scrollY;
            /* Guard against a sub-pixel negative from a mid-scroll measurement. */
            setChromeTop(Math.max(0, Math.round(top)));
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);

    const live = activeSlots(slots);
    const occupancy = resolveOccupancy({slots, stage, pinnedSlotId});

    const rail = byFootprint(live, SlotFootprint.RAIL);
    const stageSlots = byFootprint(live, SlotFootprint.STAGE);
    const columnWidgets = byFootprint(live, SlotFootprint.COLUMN_WIDGET);
    const columnOccupant = occupancy.columnOccupantId
        ? live.find(s => s.id === occupancy.columnOccupantId)
        : undefined;

    /*
     * A CANDIDATE THAT LOSES THE COLUMN FALLS INTO THE ROW BELOW.
     *
     * Only one candidate can hold the column, and the rest used to render
     * nowhere — they were not bands, so the layout simply dropped them and the
     * surface offered a switcher to bring one back. That made a layout decision
     * into a question for the viewer. `PAGE_FORM` already states the answer:
     * a COLUMN's other form is a BAND. So the losers take their page form and
     * join the band row, which is where the anchor puts them — beside the
     * streak card, not behind a toggle.
     */
    const displaced = live.filter(s =>
        occupancy.candidateIds.includes(s.id) &&
        s.id !== occupancy.columnOccupantId &&
        s.footprint !== SlotFootprint.BAND &&
        s.footprint !== SlotFootprint.HORIZONTAL_RAIL);

    const ownBands = live.filter(s =>
        s.footprint === SlotFootprint.BAND || s.footprint === SlotFootprint.HORIZONTAL_RAIL);

    /*
     * Displaced content belongs at the TOP of the band flow, not the bottom.
     *
     * It was column content — it sat beside the hero — so appending it after
     * every authored band drops it to the foot of the page, below sections it
     * has nothing to do with. The anchor keeps it in the first row under the
     * hero, alongside the streak card. So it follows the first band rather
     * than trailing the last.
     */
    /*
     * The first band and whatever was displaced share ONE ROW.
     *
     * Stacked full-width sections is not what the column's content becomes when
     * it moves — the anchor puts the streak card and the displaced cards side by
     * side in a single strip under the hero. Rendering them as consecutive bands
     * gave two full-width slabs instead, so they are grouped into a row wrapper
     * and only when there is something displaced; a surface with none keeps the
     * plain band flow untouched.
     */
    const rowBands = displaced.length > 0 && ownBands.length > 0
        ? [ownBands[0], ...displaced]
        : [];
    const bands = rowBands.length > 0 ? ownBands.slice(1) : [...ownBands, ...displaced];

    return (
        <div
            ref={rootRef}
            className="surface-hub"
            /*
             * The occupancy signal, published so descendants can answer to
             * their NEIGHBOURS in plain CSS, not just to the viewport:
             *   [data-occupancy~="column-widget"] .earn-grid { … }
             * Fit is occupancy x viewport. See occupancy.ts.
             */
            data-occupancy={occupancySignal(occupancy, slots)}
            data-stage={stage.toLowerCase()}
            style={{"--surface-chrome-top": `${chromeTop}px`} as React.CSSProperties}
        >
            {chrome}

            {/*
             * `.wrap` is `display:flow-root` — a block formatting context, so it
             * CONTAINS both floats and the page keeps its height. It must never
             * become a grid: a track reserves its width for the whole page and
             * creates the dead gutters the float model exists to avoid.
             */}
            <div className="sh-wrap surface-hub__wrap">
                {/*
                 * Left float. The rail holds its 246px only while it exists, so
                 * everything below it reclaims the full width — which is what a
                 * grid track could never do, and why the lower half of the page
                 * used to look permanently indented.
                 */}
                {rail.length > 0 &&
                    <React.Fragment>
                        {rail.map(s => <React.Fragment key={s.id}>{renderSlot(s, occupancy)}</React.Fragment>)}
                    </React.Fragment>
                }

                <div className="sh-content surface-hub__content">
                    {/*
                 * The column is emitted BEFORE the stage so the right float has
                 * content to flow past. `order` re-sequences it at <=1180px.
                 * Absent when nothing can hold it — absence is a configuration.
                 */}
                    {columnOccupant &&
                    <aside
                        className="sh-side surface-hub__column"
                        /*
                         * ADDRESSABLE, like every other placed slot. The column
                         * was the one wrapper that carried no `slot-` id, so the
                         * bar's BOARD entry — whose occupant is the column — had
                         * nothing to scroll to and selecting it moved nothing.
                         */
                        id={`slot-${columnOccupant.id}`}
                        /*
                         * The width is published as a CUSTOM PROPERTY, never as
                         * an inline `width`. An inline width cannot be overridden
                         * by a stylesheet without `!important`, so at <=1180px the
                         * column kept its 400px and hung off the right edge while
                         * every sibling went full width. Measured in a browser;
                         * jsdom cannot see it.
                         *
                         * This is the same lesson as `--colh` in FLUIDITY_CONTRACT
                         * §6 trap 1, arriving from the other direction: put the
                         * value in a property and let the cascade redefine it,
                         * rather than writing a declaration nothing can reach.
                         */
                        style={occupancy.columnWidthPx
                            ? {"--surface-col-w": `${occupancy.columnWidthPx}px`} as React.CSSProperties
                            : undefined}
                    >
                        {renderSlot(columnOccupant, occupancy)}
                        {/*
                         * UNDER the content it switches, not above it: on top it
                         * reads as page-level nav. And only where there is a
                         * real choice — one label is not a choice.
                         */}
                        {occupancy.showControl && renderColumnControl?.(occupancy)}
                    </aside>
                    }

                    <div className="sh-mid surface-hub__mid">
                        {stageSlots.map(s =>
                            <React.Fragment key={s.id}>
                                {renderSlot(s, occupancy)}
                            </React.Fragment>)}

                        {/* BEFORE the bands, so the bands flow past them. */}
                        {columnWidgets.map(s =>
                            <aside className="sh-pl sh-slot surface-hub__column-widget" key={s.id} id={`slot-${s.id}`}>
                                {renderSlot(s, occupancy)}
                            </aside>)}

                        {rowBands.length > 0 &&
                            <div className="surface-hub__bandrow">
                                {rowBands.map(s =>
                                    <section
                                        className="sh-slot surface-hub__band surface-hub__bandrow-item"
                                        key={s.id}
                                        id={`slot-${s.id}`}
                                    >
                                        {renderSlot(s, occupancy)}
                                    </section>)}
                            </div>}

                        {bands.map(s =>
                            <section
                                /*
                                 * NO `sh-pl` HERE. That is the STRIP's class, and
                                 * an occupant that is a strip carries it already —
                                 * so putting it on the wrapper too nested one
                                 * inside another and applied the strip's margin
                                 * and header spacing twice.
                                 *
                                 * The wrapper's only job is to be a formatting
                                 * context sitting in the flow; what it holds
                                 * decides how it looks.
                                 */
                                className={s.footprint === SlotFootprint.HORIZONTAL_RAIL
                                    ? "sh-slot surface-hub__band surface-hub__hrail"
                                    : "sh-slot surface-hub__band"}
                                key={s.id}
                                /* Addressable: the rail scrolls here on select. */
                                id={`slot-${s.id}`}
                            >
                                {renderSlot(s, occupancy)}
                            </section>)}
                    </div>
                </div>
            </div>

            {actionBar}
        </div>
    );
};
